import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import {
  CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { IntervalOption } from '../../services/api';
import { fetchAndroidTvActivitySeries } from '../../services/androidtvApi';
import { useDeviceChartPeriod } from '../../hooks/useDeviceChartPeriod';
import {
  formatChartTooltipTime,
  formatInServerTz,
} from '../../utils/serverTime';
import {
  ChartHoverTipBridge,
  ChartHoverTipData,
  ChartHoverTipPortal,
} from '../charts/ChartHoverTip';
import DeviceChartPeriodToolbar from '../charts/DeviceChartPeriodToolbar';

const ACCENT = '#2391FF';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(255,255,255,0.45)';
const ACTIVITY_COLOR = '#81C784';

interface Props {
  deviceId: number;
  chartHeight?: number;
}

const AndroidTvActivityChart: React.FC<Props> = ({ deviceId, chartHeight }) => {
  const { t, i18n } = useTranslation();
  const plotHeight = chartHeight ?? 105;
  const compact = plotHeight <= 140;
  const plotRef = useRef<HTMLDivElement>(null);
  const [hoverTip, setHoverTip] = useState<ChartHoverTipData | null>(null);
  const [chartInterval, setChartInterval] = useState<IntervalOption>('5minutes');
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState<Array<{ time_bucket: string; activity_ratio: number }>>([]);

  const periodCtl = useDeviceChartPeriod('today');
  const {
    period, range,
    handlePeriodChange, navigateDay, goToday,
    canNavigateNextDay, isTodayActive,
    customDateDialogOpen, customStartInput, customEndInput,
    setCustomStartInput, setCustomEndInput,
    handleCustomDateConfirm, handleCustomDateCancel,
  } = periodCtl;

  const formatTipItem = useCallback(
    (value: unknown, name: string) => {
      if (name === t('deviceModal.activity')) {
        const n = typeof value === 'number' ? value : Number(value);
        return n >= 0.5 ? t('deviceModal.on') : t('deviceModal.off');
      }
      return String(value);
    },
    [t],
  );
  const tipContent = useMemo(
    () => (
      <ChartHoverTipBridge
        formatItem={formatTipItem}
        onTip={setHoverTip}
        localeLang={i18n.language}
      />
    ),
    [formatTipItem, i18n.language],
  );

  const intervals = useMemo(
    (): IntervalOption[] => ['minute', '5minutes', '15minutes', '30minutes', 'hour'],
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAndroidTvActivitySeries({
        deviceId,
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
        interval: chartInterval,
      });
      setRaw(res.data || []);
    } catch {
      setRaw([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId, chartInterval, range.start, range.end]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartData = useMemo(() => {
    const periodHours = (range.end.getTime() - range.start.getTime()) / 3600000;
    const lang = i18n.language;
    return raw.map((row) => {
      const d = new Date(row.time_bucket);
      const label = periodHours > 6
        ? formatInServerTz(d, 'dd/MM HH:mm')
        : formatInServerTz(d, 'HH:mm');
      return {
        time: label,
        ts: d.getTime(),
        tooltipLabel: formatChartTooltipTime(d, lang),
        activity: (row.activity_ratio ?? 0) >= 0.5 ? 1 : 0,
      };
    });
  }, [raw, range.start, range.end, i18n.language]);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '20px',
        px: 1.5,
        pt: 1.1,
        pb: 1.1,
        border: `1px solid ${SURFACE_BORDER}`,
        background: `
          radial-gradient(90% 70% at 50% 0%, ${ACCENT}18 0%, transparent 55%),
          linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)
        `,
      }}
    >
      <DeviceChartPeriodToolbar
        period={period}
        chartInterval={chartInterval}
        intervals={intervals}
        onPeriodChange={handlePeriodChange}
        onIntervalChange={setChartInterval}
        onNavigateDay={navigateDay}
        onGoToday={goToday}
        onRefresh={() => { void load(); }}
        canNavigateNextDay={canNavigateNextDay}
        isTodayActive={isTodayActive}
        customDateDialogOpen={customDateDialogOpen}
        customStartInput={customStartInput}
        customEndInput={customEndInput}
        onCustomStartInputChange={setCustomStartInput}
        onCustomEndInputChange={setCustomEndInput}
        onCustomDateConfirm={handleCustomDateConfirm}
        onCustomDateCancel={handleCustomDateCancel}
      />

      <Box
        ref={plotRef}
        onMouseLeave={() => setHoverTip(null)}
        sx={{
          width: '100%',
          height: plotHeight,
          position: 'relative',
          borderRadius: '14px',
          bgcolor: 'rgba(0,0,0,0.22)',
          border: `1px solid ${SURFACE_BORDER}`,
          px: 0.5,
          pt: 0.5,
          pb: 0.5,
          overflow: 'hidden',
        }}
      >
        <ChartHoverTipPortal tip={hoverTip} anchorRef={plotRef} />
        {loading && (
          <Typography sx={{ position: 'absolute', top: 10, right: 12, fontSize: 11, color: MUTED, zIndex: 1 }}>
            …
          </Typography>
        )}
        {chartData.length === 0 && !loading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: MUTED, fontSize: 12, textAlign: 'center', px: 2 }}>
              {t('deviceModal.noActivityData')}
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: compact ? 4 : 8,
                right: 6,
                left: 0,
                bottom: compact ? 2 : 8,
              }}
            >
              <CartesianGrid strokeDasharray="4 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="time"
                tick={{ fill: MUTED, fontSize: compact ? 9 : 10 }}
                axisLine={{ stroke: SURFACE_BORDER }}
                tickLine={false}
                minTickGap={24}
                height={compact ? 18 : 30}
              />
              <YAxis
                yAxisId="activity"
                domain={[0, 1]}
                ticks={[0, 1]}
                tickFormatter={(v) => (v >= 0.5 ? t('deviceModal.on') : t('deviceModal.off'))}
                tick={{ fill: ACTIVITY_COLOR, fontSize: compact ? 9 : 10 }}
                axisLine={false}
                tickLine={false}
                width={compact ? 36 : 42}
              />
              <Tooltip
                content={tipContent}
                cursor={{ stroke: 'rgba(255,255,255,0.25)', strokeWidth: 1 }}
                wrapperStyle={{ outline: 'none', visibility: 'hidden' }}
              />
              {!compact && (
                <Legend
                  wrapperStyle={{
                    fontSize: 10.5,
                    color: MUTED,
                    paddingTop: 6,
                    lineHeight: '16px',
                    width: '100%',
                  }}
                  iconType="circle"
                  iconSize={8}
                />
              )}
              <Line
                yAxisId="activity"
                type="stepAfter"
                dataKey="activity"
                name={t('deviceModal.activity')}
                stroke={ACTIVITY_COLOR}
                strokeWidth={compact ? 2 : 2.25}
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default AndroidTvActivityChart;
